import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { syncService } from '../services/syncService';
import { ReadScope, SyncStatus, type SyncType } from '../types/sync/enums';
import { HttpError } from '../services/apiClient';
import { ApiErrorCode } from '../services/apiErrorCodes';
import { useConnectCredentialsStore } from '../stores/session/connectCredentialsStore';
import { ensureFreshToken } from '../utils/auth/ensureFreshToken';
import { useNoAiStore } from '../stores/noAiStore';
import { useSyncStore } from '../stores/syncStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import { RelanceOnboardingStatus } from '../types/onboarding';
import { useIsAcomba } from './useIsAcomba';
import { useAcombaBridgeStore } from '../stores/acombaBridgeStore';
import { useToastStore } from '../stores/toastStore';
import { useTenantStore } from '../stores/tenantStore';
import { consumeSyncStream } from '../utils/sync/consumeSyncStream';
import { extractBridgeData } from '../utils/bridge/bridgeExtraction';
import { buildBridgeSyncPayload } from '../utils/bridge/buildBridgeSyncPayload';
import { extractionIncompleteMessage } from '../utils/bridge/extractionError';
import { bridgeSyncBlockReason } from '../utils/bridge/bridgeSyncPreflight';
import { isBridgeOutdated } from '../utils/bridge/bridgeVersion';
import { TranslationKey } from '../i18n/translationKeys';
import type { SyncStatusResponse } from '../types/sync/syncStatusResponse';

export interface UseSyncResult {
    /**
     * Runs the correct sync for the active integration (Acomba bridge or AcceoConnect).
     *
     * `syncType` is deliberately not defaulted — it decides how much the backend recomputes, and a default would
     * let a new caller pick that silently. `readScope` is a separate, bridge-only axis and defaults to reading
     * everything, which is always correct and merely slower.
     */
    trigger: (syncType: SyncType, readScope?: ReadScope, regenerate?: boolean) => Promise<void>;
    syncing: boolean;
    progress: SyncStatusResponse | null;
    syncStatus: string | null;
    /** Whether a sync can be started right now (bridge reachable+connected, or a Connect token exists). */
    canSync: boolean;
}

/**
 * Single entry point for triggering a data sync from anywhere in the app (header button, settings,
 * missing-info banner). It dispatches to the Acomba bridge or the AcceoConnect path based on the
 * active integration, and shares progress/state through the sync store so every consumer stays in sync.
 */
export const useSync = (): UseSyncResult => {
    const { t } = useTranslation();
    const isAcomba = useIsAcomba();
    const acceoConnectToken = useConnectCredentialsStore(s => s.acceoConnectToken);
    const noAi = useNoAiStore(s => s.noAi);
    const { syncing, progress, syncStatus, setSyncing, setProgress, setSyncStatus, setSyncFailed } = useSyncStore();
    const bridgeStatus = useAcombaBridgeStore(s => s.bridgeStatus);
    const sessionStatus = useAcombaBridgeStore(s => s.sessionStatus);
    const isExtracting = useAcombaBridgeStore(s => s.isExtracting);
    const checkBridgeHealth = useAcombaBridgeStore(s => s.checkBridgeHealth);
    const showToast = useToastStore(s => s.showToast);
    const onboardingStatus = useOnboardingStore(s => s.status);
    const abortRef = useRef<AbortController | null>(null);

    // `syncing` only covers a sync driven through this hook. An extraction can also be in flight from
    // another entry point (the Initial Sync overlay, the Settings button, another mounted consumer), and
    // those live in a different store — so a button gated on `syncing` alone stays clickable during one.
    // Gating on `isExtracting` too is what stops a second click reaching the sync path at all; the null
    // sentinel from extractBridgeData is the backstop if one still does.
    //
    // Mirrors the backend's fail-closed onboarding gate (SyncService/BridgeSyncService) so a caller never
    // exposes a control that would only come back as a raw "onboardingNotCompleted" error — the backend
    // stays the actual enforcement (this is UX only, and fails closed the same way if the status hasn't
    // resolved yet). Does not affect the onboarding walkthrough's own trigger() call in Onboarding.tsx,
    // which records consent first and calls trigger() directly without consulting canSync.
    const canSync = onboardingStatus === RelanceOnboardingStatus.Completed && (isAcomba
        ? bridgeStatus === 'reachable' && sessionStatus === 'connected' && !isExtracting
        : !!acceoConnectToken);

    // Streams progress until a terminal snapshot, aborting any prior stream first.
    const consumeStream = useCallback(async () => {
        const controller = new AbortController();
        abortRef.current?.abort();
        abortRef.current = controller;
        await consumeSyncStream(controller, setProgress, setSyncStatus, t);
    }, [t, setProgress, setSyncStatus]);

    const reportRegenerateRefused = useCallback(() => {
        const message = t(TranslationKey.ApiErrors.SyncAlreadyRunning);
        showToast('error', message);
        setSyncStatus(message);
    }, [setSyncStatus, showToast, t]);

    const runConnect = useCallback(async (syncType: SyncType, regenerate: boolean) => {
        if (!acceoConnectToken) return;
        // Keep the desktop AcceoConnect token fresh (no-op for portal); the backend reads it from the
        // session/header — it is not passed to triggerConnect.
        try { await ensureFreshToken(); }
        catch (e) { console.error('Token refresh before sync failed:', e); return; }

        try {
            try { await syncService.triggerConnect(noAi, syncType, regenerate); }
            catch (e) {
                if (!(e instanceof HttpError && e.status === 409)) throw e; // 409 = already running
                if (regenerate) { reportRegenerateRefused(); return; }
            }
            await consumeStream();
        } catch (e) {
            setSyncStatus(t(TranslationKey.Settings.SyncError, { message: (e as Error).message }));
        }
    }, [acceoConnectToken, noAi, consumeStream, reportRegenerateRefused, setSyncStatus, t]);

    const runBridge = useCallback(async (syncType: SyncType, readScope: ReadScope, regenerate: boolean) => {
        // Re-verify the bridge directly — the health poller may be stale between ticks.
        await checkBridgeHealth();
        const bridge = useAcombaBridgeStore.getState();
        if (bridge.bridgeStatus !== 'reachable') { setSyncStatus(t(TranslationKey.InitialSync.BridgeUnreachableError)); return; }
        if (bridge.sessionStatus !== 'connected') { setSyncStatus(t(TranslationKey.InitialSync.BridgeNoSessionError)); return; }
        // Refuse sync when the bridge (bundled with Acomba) is older than this app supports — the user
        // must update Acomba. Acomba-only; the AcceoConnect path (runConnect) is unaffected.
        if (isBridgeOutdated(bridge.bridgeInfo?.bridgeVersion)) { setSyncStatus(t(TranslationKey.AcombaBridge.Errors.BridgeOutdated)); return; }

        // Soft per-functionality gate (Acomba only): skip the relance sync when the connected user lacks
        // the required Acomba privileges. Unknown access (null) is not blocked. AcombaGo is unaffected.
        if (bridge.functionalityAccess && !bridge.functionalityAccess.relance) {
            setSyncStatus(t(TranslationKey.AcombaBridge.Errors.RelanceAccessDenied));
            return;
        }

        // Block (with feedback) before touching the bridge when the sync can't produce a correct result:
        // a stale copy would have a Full sync overwrite newer data with older (BR-19 made this the risk;
        // it used to be that an incremental read sent nothing), and a company mismatch would push a
        // foreign dataset. Failing silently here is exactly the confusing "sync did nothing" the user hits.
        const blocked = bridgeSyncBlockReason(
            { isStaleCopy: useTenantStore.getState().isStaleCopy, companyMatchStatus: bridge.companyMatchStatus },
            t,
        );
        // Toast for immediate attention + persistent syncStatus so the reason survives the toast's 4s
        // dismissal (Settings shows syncStatus by the sync button, falling back to generic text if null).
        if (blocked) { showToast('error', blocked); setSyncStatus(blocked); return; }

        try {
            // Partial → incremental (modified-since) extraction when a prior sync date exists;
            // Full (or first-ever sync) → full extraction. Shared with the auto-sync path so the
            // decision can't drift. Abort the sync if any interface failed to read — a partial payload
            // would produce wrong relances (and on a Full sync could wipe them). Keep the last-good
            // data; inform via toast.
            const extraction = await extractBridgeData(syncType, readScope);
            // No extraction ran — one was already in flight, or the bridge is on a different company.
            // Abort rather than fall through: buildBridgeSyncPayload reads the store, so continuing would
            // post whatever a previous extraction left there. This is the case a `[]` return used to hide.
            if (extraction === null) {
                const message = t(TranslationKey.Settings.SyncFailed);
                showToast('error', message);
                setSyncStatus(message);
                return;
            }
            if (extraction.failed.length > 0) {
                const message = extractionIncompleteMessage(extraction.failed, t);
                showToast('error', message);
                setSyncStatus(message);
                return;
            }
            const payload = buildBridgeSyncPayload(syncType, extraction.isCompleteRead);
            if (!payload) { setSyncStatus(t(TranslationKey.Settings.SyncFailed)); return; }

            try {
                await syncService.triggerBridge(payload, regenerate);
                // Deliberately does NOT clear isStaleCopy here. It cannot be reached with the flag set —
                // bridgeSyncBlockReason returns above whenever it is true — so a clear here only ever ran
                // as a no-op while pretending to be a recovery path. A stale copy is cleared by an explicit
                // promote or by the next desktop-auth, which is what BR-10 says and what the banner offers.
                //
                // This is not an oversight to "fix" by clearing anyway: re-syncing must not silently undo a
                // stale-copy verdict, because a Full sync from an older copy is how its data becomes truth.
            } catch (e) {
                // The connected copy's posted history is behind the one we last synced — a restored backup
                // or an older company. Deliberately NOT offered as a full sync: rebuilding would make the
                // older data the accepted truth and silently drop everything newer. Cleared only by an
                // explicit promote, which is the action the banner offers.
                if (e instanceof HttpError && e.code === ApiErrorCode.BridgeStaleCopy) {
                    useTenantStore.getState().setStaleCopy(true);
                    setSyncStatus(t(TranslationKey.AcombaBridge.Errors.StaleCopySync));
                    return;
                }
                if (!(e instanceof HttpError && e.status === 409)) throw e; // 409 = already running
                if (regenerate) { reportRegenerateRefused(); return; }
            }
            await consumeStream();
        } catch (e) {
            setSyncStatus(t(TranslationKey.Settings.SyncError, { message: (e as Error).message }));
        }
    }, [checkBridgeHealth, consumeStream, reportRegenerateRefused, setSyncStatus, showToast, t]);

    const trigger = useCallback(async (syncType: SyncType, readScope: ReadScope = ReadScope.Complete, regenerate = false) => {
        setSyncing(true);
        setSyncStatus(null);
        setSyncFailed(false);
        setProgress(null);
        try {
            // readScope is bridge-only: AcceoConnect decides its own fetch window from the sync type.
            if (isAcomba) await runBridge(syncType, readScope, regenerate);
            else await runConnect(syncType, regenerate);
        } finally {
            setSyncing(false);
            // A trigger() call always represents an attempted sync, so anything short of a Completed
            // snapshot counts as a failure — including a silent early return (no Connect token, a failed
            // token refresh) or a stream that ends without ever reaching a terminal snapshot, neither of
            // which reaches one of the branches above that set syncStatus explicitly.
            const { progress: finished } = useSyncStore.getState();
            setSyncFailed(finished?.status !== SyncStatus.Completed);
        }
    }, [isAcomba, runBridge, runConnect, setSyncing, setSyncStatus, setSyncFailed, setProgress]);

    return { syncing, progress, syncStatus, trigger, canSync };
};
